import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import BottomNavigation from '../../components/common/BottomNavigation';
import { useApp } from '../../context/AppContext';
import { useData } from '../../context/DataContext';
import { useCart } from '../../context/CartContext';
import { useOrder } from '../../context/OrderContext';
import { useAuth } from '../../context/AuthContext';
import { useTable } from '../../context/TableContext';
import { generateOrderId } from '../../utils/idGenerator';

const CustomerCheckout: React.FC = () => {
  const { setCurrentScreen } = useApp();
  const { products, orders, updateOrders, reduceProductStock } = useData();
  const { getCartItems, getCartTotal, clearCart } = useCart();
  const { addOrder } = useOrder();
  const { user } = useAuth();
  const { selectedTable } = useTable();
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [showBillModal, setShowBillModal] = useState(false);
  const [completedOrder, setCompletedOrder] = useState<any>(null);

  // Debug logging
  useEffect(() => {
    console.log('🛒 CustomerCheckout: selectedTable:', selectedTable);
    console.log('🛒 CustomerCheckout: user:', user);
  }, [selectedTable, user]);

  const handleBack = () => {
    setCurrentScreen('customer-home');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Table should always be selected at login, but add safety check
    if (!selectedTable) {
      alert('No table selected. Please logout and login again.');
      setCurrentScreen('customer-home');
      return;
    }
    
    setIsProcessing(true);
    
    const cartItems = getCartItems(products);
    const insufficientStock = cartItems.some(({ product, quantity }) => product.stock < quantity);
    
    if (insufficientStock) {
      alert('Some items in your cart are out of stock.');
      setIsProcessing(false);
      return;
    }
    
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    const orderItems = cartItems.map(({ product, quantity }) => ({
      id: product.id,
      productName: product.name,
      quantity,
      price: product.price
    }));
    
    const orderData = {
      id: generateOrderId(),
      customerName: selectedTable?.currentCustomerName || user?.name || 'Customer',
      customerId: user?.customerId || user?.id || 'unknown',
      amount: subtotal * 1.15,
      status: 'pending' as const,
      date: new Date().toISOString(),
      items: orderItems,
      paymentMethod: 'pending',
      transactionId: null,
      tableNumber: selectedTable?.tableNumber || 'N/A',
      tableLocation: selectedTable?.location || 'N/A',
      tableId: selectedTable?.id,
      isPaid: false
    };
    
    const newOrder = await addOrder(orderData);
    updateOrders([newOrder, ...orders]);
    
    const stockReductionItems = orderItems.map(item => ({
      id: item.id,
      quantity: item.quantity
    }));
    await reduceProductStock(stockReductionItems);
    
    clearCart();
    setCompletedOrder(newOrder);
    setShowBillModal(true);
    setIsProcessing(false);
  };

  const cartItems = getCartItems(products);
  const subtotal = getCartTotal(products);
  const total = subtotal;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900 pb-32">
      <div className="sticky top-0 z-40 backdrop-blur-lg bg-slate-900 bg-opacity-80 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center py-4">
            <button onClick={handleBack} className="p-2 text-gray-400 hover:text-white mr-4 transition-colors">
              <ArrowLeft size={24} />
            </button>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent">
              Checkout
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="space-y-6">
          {selectedTable && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="gradient-bg rounded-2xl p-6 shadow-xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white text-sm opacity-90 mb-1">Dining at</p>
                  <h3 className="text-white font-bold text-2xl">{selectedTable.tableNumber}</h3>
                  <p className="text-white text-sm opacity-90">{selectedTable.location} • {selectedTable.capacity} seats</p>
                </div>
                <div className="text-6xl">🪑</div>
              </div>
            </motion.div>
          )}

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="food-card">
            <h2 className="text-2xl font-bold text-white mb-6">Order Summary</h2>
            <div className="space-y-4">
              {cartItems.map(({ product, quantity }) => product && (
                <div key={product.id} className="flex items-center space-x-4 bg-slate-700 rounded-xl p-4">
                  <img src={product.image} alt={product.name} className="w-16 h-16 rounded-xl object-cover" />
                  <div className="flex-1">
                    <h3 className="text-white font-semibold text-lg">{product.name}</h3>
                    <p className="text-gray-400 text-sm">Qty: {quantity}</p>
                  </div>
                  <p className="text-orange-400 font-bold text-lg">${(product.price * quantity).toFixed(2)}</p>
                </div>
              ))}
            </div>
            <div className="border-t border-gray-700 mt-6 pt-6 space-y-3">
              <div className="space-y-3">
                <div className="flex justify-between text-gray-300 text-lg">
                  <span>Subtotal</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-300">
                  <span>GST (5%)</span>
                  <span>${(subtotal * 0.05).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-300">
                  <span>Service Charge (10%)</span>
                  <span>${(subtotal * 0.10).toFixed(2)}</span>
                </div>
              </div>
              <div className="border-t border-gray-600 pt-4 flex justify-between text-white font-bold text-2xl">
                <span>Total Amount</span>
                <span className="text-orange-400">${(subtotal * 1.15).toFixed(2)}</span>
              </div>
            </div>
          </motion.div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="food-card">
              <div className="flex items-center justify-between mb-6">
                <span className="text-white font-semibold text-xl">Total Amount</span>
                <span className="text-orange-400 font-bold text-3xl">${total.toFixed(2)}</span>
              </div>

            </motion.div>
          </form>
        </div>
      </div>

      {/* Fixed Checkout Button */}
      <div className="fixed bottom-20 left-0 right-0 z-40 px-4 pb-4 bg-gradient-to-t from-gray-900 via-gray-900 to-transparent pt-6">
        <div className="max-w-7xl mx-auto">
          <motion.button 
            onClick={handleSubmit}
            disabled={isProcessing || !selectedTable} 
            whileHover={{ scale: (!selectedTable || isProcessing) ? 1 : 1.02 }} 
            whileTap={{ scale: (!selectedTable || isProcessing) ? 1 : 0.98 }} 
            className={`w-full font-bold py-5 px-6 rounded-xl transition-all duration-200 text-lg shadow-2xl ${!selectedTable ? 'bg-red-600 text-white cursor-not-allowed' : 'btn-primary disabled:bg-gray-600 disabled:cursor-not-allowed'}`}
          >
            {isProcessing ? 'Processing...' : !selectedTable ? '⚠️ Please Select Your Table From Home' : `Place Order - $${total.toFixed(2)}`}
          </motion.button>
        </div>
      </div>

      <BottomNavigation />

      {showBillModal && completedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="bg-slate-800 rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="gradient-bg p-8 text-center">
              <div className="text-7xl mb-4">✅</div>
              <h2 className="text-3xl font-bold text-white mb-3">Order Placed!</h2>
              <p className="text-white text-lg opacity-90">Order #{completedOrder.id}</p>
            </div>
            <div className="p-6 space-y-4">
              {selectedTable && (
                <div className="bg-slate-700 rounded-xl p-4">
                  <p className="text-gray-400 text-xs mb-1">Dining at</p>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-white font-bold text-lg">{selectedTable.tableNumber}</h3>
                      <p className="text-gray-300 text-sm">{selectedTable.location}</p>
                    </div>
                    <div className="text-3xl">🪑</div>
                  </div>
                </div>
              )}
              <div className="border-b border-gray-700 pb-4">
                <p className="text-gray-400 text-xs mb-2">Customer Details</p>
                <p className="text-white font-medium">{completedOrder.customerName}</p>
                <p className="text-gray-300 text-sm">ID: {completedOrder.customerId}</p>
                <p className="text-gray-400 text-xs mt-1">{new Date(completedOrder.date).toLocaleString()}</p>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-3">Order Items</h3>
                <div className="space-y-2">
                  {completedOrder.items.map((item: any, index: number) => (
                    <div key={index} className="flex justify-between text-sm">
                      <span className="text-gray-300">{item.productName} x {item.quantity}</span>
                      <span className="text-white font-medium">${(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="border-t border-gray-700 pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Subtotal</span>
                  <span className="text-gray-300">${(completedOrder.amount / 1.15).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">GST (5%)</span>
                  <span className="text-gray-300">${((completedOrder.amount / 1.15) * 0.05).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Service Charge (10%)</span>
                  <span className="text-gray-300">${((completedOrder.amount / 1.15) * 0.10).toFixed(2)}</span>
                </div>
                <div className="border-t border-gray-600 pt-3 flex justify-between">
                  <span className="text-white font-bold text-xl">Total Amount</span>
                  <span className="text-orange-400 font-bold text-2xl">${completedOrder.amount.toFixed(2)}</span>
                </div>
              </div>
              <div className="bg-slate-700 rounded-xl p-3">
                <p className="text-gray-400 text-xs mb-1">Payment Status</p>
                <p className="text-yellow-400 font-medium">Payment will be collected at the table</p>
              </div>
              <div className="space-y-3 pt-4">
                <button onClick={() => { setShowBillModal(false); setCurrentScreen('customer-track'); }} className="w-full btn-primary py-4 text-lg">
                  Track Order
                </button>
                <button onClick={() => { setShowBillModal(false); setCurrentScreen('customer-home'); }} className="w-full btn-secondary py-4 text-lg">
                  Back to Home
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default CustomerCheckout;
